import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box, Stack, Typography, Button, IconButton, Card, CardContent, CardActionArea,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Divider, Chip, Tooltip, CircularProgress, Alert, Paper, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import TodayIcon from '@mui/icons-material/Today';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import SendIcon from '@mui/icons-material/Send';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  isPushSupported,
  isAlreadySubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  getPermission,
} from '../lib/push';

const API = 'https://portal.peaksoftcon.com:8081/api';
const WEEKDAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

// ── Date helpers (local time, no library) ──────────────────────────
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const pad = (n) => String(n).padStart(2, '0');
const fmtISODate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Grid starts on Monday — Turkish convention.
function startOfGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const dow = first.getDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDays(first, offset);
}
function parseRemindAt(s) {
  // Backend sends MySQL DATETIME like "2026-05-14 09:30:00".
  // Treat as local time (server runs Europe/Istanbul same as users).
  if (!s) return new Date(NaN);
  const m = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/.exec(s);
  if (!m) return new Date(s);
  return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
}
function fmtTime(d) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtDateLong(d) {
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`;
}
// Tamamlanan (yeşil) hatırlatıcılar her listenin altına iner; grup
// içinde kronolojik sıra (en erken üstte) korunur.
function sortByCompletion(arr) {
  return [...arr].sort((a, b) => {
    const done = (a.completed ? 1 : 0) - (b.completed ? 1 : 0);
    if (done !== 0) return done;
    return parseRemindAt(a.remind_at) - parseRemindAt(b.remind_at);
  });
}

const blankForm = { id: null, title: '', description: '', date: '', time: '', target_company_id: '' };

export default function Reminders() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const info = (() => {
    try { return JSON.parse(localStorage.getItem('companyInfo') || 'null'); }
    catch { return null; }
  })();
  const companyID = info?.ID;
  const isAdmin = info?.role === 'Admin';

  const [displayedMonth, setDisplayedMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [reminders, setReminders] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Push state
  const [pushSupported] = useState(() => isPushSupported());
  const [pushPermission, setPushPermission] = useState(() => getPermission());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  // Manual push state (admin-only)
  const [pushForm, setPushForm] = useState({ target: '', title: '', body: '' });
  const [pushSending, setPushSending] = useState(false);
  const [pushSendResult, setPushSendResult] = useState(null);

  useEffect(() => {
    let mounted = true;
    isAlreadySubscribed().then((v) => mounted && setPushSubscribed(v));
    return () => { mounted = false; };
  }, []);

  const handleTogglePush = async () => {
    setPushError('');
    setPushBusy(true);
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        setPushSubscribed(false);
      } else {
        await subscribeToPush();
        setPushSubscribed(true);
      }
      setPushPermission(getPermission());
    } catch (e) {
      setPushError(e.message || String(e));
    } finally {
      setPushBusy(false);
    }
  };

  // ── Data loading ────────────────────────────────────────────────
  const loadMonth = useCallback(async () => {
    if (!companyID) return;
    setLoading(true);
    try {
      const from = fmtISODate(startOfMonth(displayedMonth));
      const to = fmtISODate(endOfMonth(displayedMonth));
      const { data } = await axios.get(`${API}/reminders`, {
        params: { companyID, from, to },
      });
      setReminders(data || []);
    } catch (err) {
      console.error('reminders load error:', err);
    } finally {
      setLoading(false);
    }
  }, [companyID, displayedMonth]);

  const loadUpcoming = useCallback(async () => {
    if (!companyID) return;
    try {
      const today = new Date();
      const from = fmtISODate(today);
      const to = fmtISODate(addDays(today, 30));
      const { data } = await axios.get(`${API}/reminders`, {
        params: { companyID, from, to },
      });
      setUpcoming(data || []);
    } catch (err) {
      console.error('upcoming load error:', err);
    }
  }, [companyID]);

  const loadCompanies = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axios.get(`${API}/companies`);
      setCompanies((data || []).filter((c) => c.role === 'Client'));
    } catch (err) {
      console.error('companies load error:', err);
    }
  }, [isAdmin]);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadUpcoming(); }, [loadUpcoming]);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  // ── Calendar grid ───────────────────────────────────────────────
  const cells = useMemo(() => {
    const start = startOfGrid(displayedMonth);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [displayedMonth]);

  // Bucket reminders by yyyy-mm-dd for fast lookup in the grid.
  const byDay = useMemo(() => {
    const m = new Map();
    for (const r of reminders) {
      const dt = parseRemindAt(r.remind_at);
      const key = fmtISODate(dt);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push({ ...r, _dt: dt });
    }
    // Sort each day chronologically.
    for (const arr of m.values()) arr.sort((a, b) => a._dt - b._dt);
    return m;
  }, [reminders]);

  const remindersForDay = useMemo(
    () => sortByCompletion(byDay.get(fmtISODate(selectedDay)) || []),
    [byDay, selectedDay],
  );

  // ── Form / dialog ───────────────────────────────────────────────
  const openCreate = (forDate = null) => {
    const base = forDate || selectedDay || new Date();
    setIsEditing(false);
    setForm({
      ...blankForm,
      date: fmtISODate(base),
      time: '09:00',
    });
    setOpen(true);
  };

  const openEdit = (r) => {
    const dt = parseRemindAt(r.remind_at);
    setIsEditing(true);
    setForm({
      id: r.id,
      title: r.title || '',
      description: r.description || '',
      date: fmtISODate(dt),
      time: fmtTime(dt),
      target_company_id: r.target_company_id ? String(r.target_company_id) : '',
    });
    setOpen(true);
  };

  const onFormChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const canModify = (r) => {
    if (isAdmin) return true;
    // Client may only edit/delete their own personal (no-target) reminders.
    return r.created_by_company_id === companyID && !r.target_company_id;
  };

  const submit = async () => {
    if (!form.title.trim() || !form.date || !form.time) {
      alert('Başlık, tarih ve saat zorunludur.');
      return;
    }
    setSaving(true);
    try {
      const remind_at = `${form.date} ${form.time}:00`;
      const body = {
        companyID,
        title: form.title.trim(),
        description: form.description || null,
        remind_at,
        target_company_id: form.target_company_id || null,
      };
      if (isEditing) {
        await axios.put(`${API}/reminders/${form.id}`, body);
      } else {
        await axios.post(`${API}/reminders`, body);
      }
      setOpen(false);
      await Promise.all([loadMonth(), loadUpcoming()]);
    } catch (err) {
      alert('Kaydetme hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const sendManualPush = async () => {
    if (!pushForm.target || !pushForm.title.trim()) {
      setPushSendResult({ severity: 'warning', message: 'Firma ve başlık zorunlu.' });
      return;
    }
    setPushSending(true);
    setPushSendResult(null);
    try {
      const { data } = await axios.post(`${API}/push/send`, {
        companyID,
        target_company_id: pushForm.target,
        title: pushForm.title.trim(),
        body: pushForm.body || '',
      });
      const targetName = companies.find((c) => String(c.ID) === String(pushForm.target))?.Company_Name || 'firma';
      setPushSendResult({
        severity: data.sent > 0 ? 'success' : 'warning',
        message:
          data.sent > 0
            ? `${targetName} ve admin için ${data.sent}/${data.total} cihaza gönderildi.`
            : 'Hiç abone cihaz bulunamadı — alıcılar henüz "Bildirimleri Aç"a basmamış.',
      });
      setPushForm({ target: '', title: '', body: '' });
    } catch (err) {
      setPushSendResult({
        severity: 'error',
        message: 'Gönderim hatası: ' + (err.response?.data?.error || err.message),
      });
    } finally {
      setPushSending(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`"${r.title}" hatırlatıcısını silmek istiyor musun?`)) return;
    try {
      await axios.delete(`${API}/reminders/${r.id}`, { params: { companyID } });
      await Promise.all([loadMonth(), loadUpcoming()]);
    } catch (err) {
      alert('Silme hatası: ' + (err.response?.data?.error || err.message));
    }
  };

  // Anyone who can see a reminder (admin, creator, or target) can toggle
  // its completed flag. Backend enforces this in the PUT path.
  const toggleComplete = async (r) => {
    const next = r.completed ? 0 : 1;
    // Optimistic update — flip locally before the server confirms so the
    // UI feels snappy. Roll back on error.
    setReminders((arr) => arr.map((x) => (x.id === r.id ? { ...x, completed: next } : x)));
    setUpcoming((arr) => arr.map((x) => (x.id === r.id ? { ...x, completed: next } : x)));
    try {
      await axios.put(`${API}/reminders/${r.id}`, { companyID, completed: next });
    } catch (err) {
      // Roll back
      setReminders((arr) => arr.map((x) => (x.id === r.id ? { ...x, completed: r.completed } : x)));
      setUpcoming((arr) => arr.map((x) => (x.id === r.id ? { ...x, completed: r.completed } : x)));
      alert('Tamamlandı işaretlenemedi: ' + (err.response?.data?.error || err.message));
    }
  };

  const goPrev = () => setDisplayedMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const goNext = () => setDisplayedMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToday = () => {
    const t = new Date();
    setDisplayedMonth(startOfMonth(t));
    setSelectedDay(t);
  };

  const today = new Date();
  const monthName = MONTHS_TR[displayedMonth.getMonth()];

  // ── Render ──────────────────────────────────────────────────────
  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        mb={2}
      >
        <Typography variant="h4" sx={{ flex: { sm: 1 } }}>Hatırlatıcılar</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {pushSupported && (
            <Tooltip
              title={
                pushPermission === 'denied'
                  ? 'Tarayıcı ayarlarından izin reddedilmiş'
                  : pushSubscribed
                    ? 'Bildirimler açık (kapatmak için tıkla)'
                    : 'Push bildirimlerini aç'
              }
            >
              <span>
                <Button
                  size="small"
                  variant={pushSubscribed ? 'contained' : 'outlined'}
                  color={pushSubscribed ? 'success' : 'primary'}
                  startIcon={pushSubscribed ? <NotificationsActiveIcon /> : <NotificationsOffIcon />}
                  onClick={handleTogglePush}
                  disabled={pushBusy || pushPermission === 'denied'}
                >
                  {pushSubscribed ? 'Bildirim Açık' : 'Bildirimleri Aç'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate()}>
            Yeni
          </Button>
        </Stack>
      </Stack>

      {pushError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPushError('')}>
          {pushError}
        </Alert>
      )}
      {pushSupported && pushPermission === 'denied' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Bildirim izni tarayıcıdan kapalı. Açmak için tarayıcı ayarlarına
          gir → bu site için bildirimleri "İzin ver" olarak değiştir.
        </Alert>
      )}
      {!pushSupported && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Bu cihaz push bildirimi desteklemiyor. iOS'ta önce "Ana ekrana
          ekle" ile uygulamayı yükle, sonra bu sayfayı aç.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
        }}
      >
        {/* ── Calendar ─────────────────────────────────────────── */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <IconButton size="small" onClick={goPrev}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>
                {monthName} {displayedMonth.getFullYear()}
              </Typography>
              <IconButton size="small" onClick={goNext}>
                <ChevronRightIcon />
              </IconButton>
              <Tooltip title="Bugüne dön">
                <IconButton size="small" onClick={goToday}>
                  <TodayIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.5,
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 700,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              {WEEKDAYS_TR.map((w) => <Box key={w}>{w}</Box>)}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.5,
              }}
            >
              {cells.map((d) => {
                const inMonth = d.getMonth() === displayedMonth.getMonth();
                const isToday = sameDay(d, today);
                const isSelected = sameDay(d, selectedDay);
                const dayList = byDay.get(fmtISODate(d)) || [];
                const hasAny = dayList.length > 0;
                return (
                  <Box
                    key={d.getTime()}
                    onClick={() => setSelectedDay(d)}
                    sx={{
                      cursor: 'pointer',
                      aspectRatio: '1 / 1',
                      borderRadius: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      bgcolor: isSelected
                        ? 'primary.main'
                        : (hasAny && inMonth ? 'action.selected' : 'transparent'),
                      color: isSelected
                        ? 'primary.contrastText'
                        : (inMonth ? 'text.primary' : 'text.disabled'),
                      border: isToday && !isSelected ? 2 : 1,
                      borderColor: isToday && !isSelected
                        ? 'primary.main'
                        : 'divider',
                      transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: isToday || isSelected ? 700 : 500, fontSize: { xs: 13, md: 14 } }}
                    >
                      {d.getDate()}
                    </Typography>
                    {hasAny && (
                      <Box
                        sx={{
                          mt: 0.25,
                          display: 'flex',
                          gap: 0.25,
                          alignItems: 'center',
                        }}
                      >
                        {dayList.slice(0, 3).map((r) => (
                          <Box
                            key={r.id}
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              bgcolor: isSelected ? 'primary.contrastText' : 'primary.main',
                            }}
                          />
                        ))}
                        {dayList.length > 3 && (
                          <Typography
                            sx={{
                              fontSize: 9,
                              lineHeight: 1,
                              color: isSelected ? 'primary.contrastText' : 'primary.main',
                            }}
                          >
                            +
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            {loading && (
              <Stack direction="row" justifyContent="center" mt={1}>
                <CircularProgress size={18} />
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* ── Selected day reminders ───────────────────────────── */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" mb={1.5}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                {fmtDateLong(selectedDay)}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => openCreate(selectedDay)}
              >
                Ekle
              </Button>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            {remindersForDay.length === 0 ? (
              <Stack alignItems="center" spacing={1} py={3} color="text.secondary">
                <EventBusyIcon fontSize="large" />
                <Typography variant="body2">Bu güne hatırlatıcı yok.</Typography>
              </Stack>
            ) : (
              <Stack spacing={1}>
                {remindersForDay.map((r) => (
                  <ReminderItem
                    key={r.id}
                    r={r}
                    onEdit={openEdit}
                    onDelete={remove}
                    onToggleComplete={toggleComplete}
                    canModify={canModify(r)}
                    canToggle
                    showDate={false}
                  />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ── Upcoming list ─────────────────────────────────────── */}
      {/* Admin sees one combined upcoming list. Client sees two: */}
      {/*  1) Adminin sana atadığı (admin tarafından target=me yapılmış) */}
      {/*  2) Senin hatırlatıcıların (kendi oluşturduğun) */}
      <Box sx={{ mt: 2 }}>
        {isAdmin ? (
          <UpcomingSection
            title="Yaklaşan (30 gün)"
            items={upcoming}
            onEdit={openEdit}
            onDelete={remove}
            onToggleComplete={toggleComplete}
            canModify={canModify}
            jumpTo={(r) => {
              const dt = parseRemindAt(r.remind_at);
              setDisplayedMonth(startOfMonth(dt));
              setSelectedDay(dt);
            }}
            emptyText="Önümüzdeki 30 gün için hatırlatıcı yok."
          />
        ) : (
          <Stack spacing={2}>
            <UpcomingSection
              title="Adminin sana atadığı (30 gün)"
              items={upcoming.filter(
                (r) => r.target_company_id === companyID && r.created_by_company_id !== companyID,
              )}
              onEdit={openEdit}
              onDelete={remove}
              onToggleComplete={toggleComplete}
              canModify={canModify}
              jumpTo={(r) => {
                const dt = parseRemindAt(r.remind_at);
                setDisplayedMonth(startOfMonth(dt));
                setSelectedDay(dt);
              }}
              emptyText="Sana atanmış bekleyen hatırlatıcı yok."
            />
            <UpcomingSection
              title="Senin hatırlatıcıların (30 gün)"
              items={upcoming.filter((r) => r.created_by_company_id === companyID)}
              onEdit={openEdit}
              onDelete={remove}
              onToggleComplete={toggleComplete}
              canModify={canModify}
              jumpTo={(r) => {
                const dt = parseRemindAt(r.remind_at);
                setDisplayedMonth(startOfMonth(dt));
                setSelectedDay(dt);
              }}
              emptyText="Kendi oluşturduğun hatırlatıcı yok."
            />
          </Stack>
        )}
      </Box>

      {/* ── Manuel Anlık Bildirim (admin-only) ────────────────── */}
      {isAdmin && (
        <Box sx={{ mt: 2 }}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <CampaignIcon color="primary" />
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Anlık Bildirim
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Seçtiğin firmaya ve sana (admin) anında push gönderir.
                Hatırlatıcıların aksine zaman planlaması yoktur — hemen iletilir.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  select
                  label="Firma"
                  value={pushForm.target}
                  onChange={(e) => setPushForm((f) => ({ ...f, target: e.target.value }))}
                  required
                  fullWidth
                >
                  <MenuItem value=""><em>Seç...</em></MenuItem>
                  {companies.map((c) => (
                    <MenuItem key={c.ID} value={String(c.ID)}>{c.Company_Name}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Başlık"
                  value={pushForm.title}
                  onChange={(e) => setPushForm((f) => ({ ...f, title: e.target.value }))}
                  required
                  fullWidth
                  inputProps={{ maxLength: 100 }}
                />
                <TextField
                  label="Mesaj (opsiyonel)"
                  value={pushForm.body}
                  onChange={(e) => setPushForm((f) => ({ ...f, body: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={5}
                  inputProps={{ maxLength: 500 }}
                />
                {pushSendResult && (
                  <Alert severity={pushSendResult.severity} onClose={() => setPushSendResult(null)}>
                    {pushSendResult.message}
                  </Alert>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={pushSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    onClick={sendManualPush}
                    disabled={pushSending || !pushForm.target || !pushForm.title.trim()}
                    fullWidth={!isDesktop}
                  >
                    {pushSending ? 'Gönderiliyor...' : 'Bildirim Gönder'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ── Create / Edit dialog ──────────────────────────────── */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth fullScreen={!isDesktop}>
        <DialogTitle>{isEditing ? 'Hatırlatıcıyı Düzenle' : 'Yeni Hatırlatıcı'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Başlık"
            value={form.title}
            onChange={onFormChange('title')}
            fullWidth margin="dense" required autoFocus
          />
          <TextField
            label="Açıklama (opsiyonel)"
            value={form.description}
            onChange={onFormChange('description')}
            fullWidth margin="dense" multiline minRows={2} maxRows={5}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tarih"
              type="date"
              value={form.date}
              onChange={onFormChange('date')}
              fullWidth InputLabelProps={{ shrink: true }} required
            />
            <TextField
              label="Saat"
              type="time"
              value={form.time}
              onChange={onFormChange('time')}
              fullWidth InputLabelProps={{ shrink: true }} required
            />
          </Stack>
          {isAdmin && (
            <TextField
              select
              label="Firma (opsiyonel)"
              value={form.target_company_id}
              onChange={onFormChange('target_company_id')}
              fullWidth margin="dense"
              helperText="Bir firma seçersen sadece sen ve firma görür + bildirim alır. Boş = kişisel."
              sx={{ mt: 2 }}
            >
              <MenuItem value=""><em>Kişisel</em></MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.ID} value={String(c.ID)}>{c.Company_Name}</MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>İptal</Button>
          <Button onClick={submit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function UpcomingSection({ title, items, onEdit, onDelete, onToggleComplete, canModify, jumpTo, emptyText }) {
  const sorted = sortByCompletion(items);
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1.5 }}>{title}</Typography>
        <Divider sx={{ mb: 1.5 }} />
        {sorted.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            {emptyText}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {sorted.map((r) => (
              <ReminderItem
                key={r.id}
                r={r}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleComplete={onToggleComplete}
                canModify={canModify(r)}
                canToggle
                showDate
                onClick={() => jumpTo(r)}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function ReminderItem({ r, onEdit, onDelete, onToggleComplete, canModify, canToggle, showDate, onClick }) {
  const dt = parseRemindAt(r.remind_at);
  const inPast = dt < new Date();
  const done = !!r.completed;
  const Wrapper = onClick ? CardActionArea : Box;
  return (
    <Paper
      variant="outlined"
      sx={{
        opacity: done ? 0.6 : (inPast || r.notified ? 0.85 : 1),
        borderLeft: 4,
        borderLeftColor: done
          ? 'success.main'
          : r.notified
            ? 'info.main'
            : (inPast ? 'grey.500' : 'primary.main'),
        transition: 'opacity 0.2s, border-color 0.2s',
      }}
    >
      <Wrapper onClick={onClick} sx={{ display: 'block', p: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {canToggle && (
            <Tooltip title={done ? 'Tamamlandı işaretini kaldır' : 'Tamamlandı olarak işaretle'}>
              <IconButton
                size="small"
                color={done ? 'success' : 'default'}
                onClick={(e) => { e.stopPropagation(); onToggleComplete(r); }}
                sx={{ mt: -0.5 }}
              >
                {done ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {showDate ? `${fmtDateLong(dt)} · ${fmtTime(dt)}` : fmtTime(dt)}
              </Typography>
              {done ? (
                <Chip size="small" color="success" label="Tamamlandı" />
              ) : r.notified ? (
                <Chip size="small" color="info" label="Bildirildi" />
              ) : inPast ? (
                <Chip size="small" color="default" label="Geçmiş" />
              ) : null}
              {r.target_name && (
                <Chip size="small" variant="outlined" label={r.target_name} />
              )}
            </Stack>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                mt: 0.25,
                textDecoration: done ? 'line-through' : 'none',
                color: done ? 'text.secondary' : 'text.primary',
              }}
            >
              {r.title}
            </Typography>
            {r.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.25,
                  whiteSpace: 'pre-wrap',
                  textDecoration: done ? 'line-through' : 'none',
                }}
              >
                {r.description}
              </Typography>
            )}
          </Box>
          {canModify && (
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(r); }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(r); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Wrapper>
    </Paper>
  );
}
